import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../context/UserContext';
import './TradeLab.css';

function Avatar({ avatar, name, size = 32 }) {
  if (avatar) return <img className="avatar" src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} style={{ width: size, height: size }} onError={e => e.target.style.display = 'none'} />;
  return <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.32 }}>{name?.slice(0,2).toUpperCase() || '??'}</div>;
}

const POS_COLORS = {
  QB: 'var(--accent2)', RB: '#00e676', WR: 'var(--accent)', TE: '#ffd32a', K: '#8b90a0', DEF: '#a855f7',
};

const GRADE_COLORS = {
  'Win': '#00e676', 'Slight Win': '#52d98a',
  'Even': '#ffd32a',
  'Slight Loss': 'var(--accent2)', 'Clear Loss': '#ff4757',
};

const FAIRNESS_COLORS = {
  'Even': '#ffd32a',
  'Slight Win A': '#00e676', 'Slight Win B': 'var(--accent)',
  'Clear Win A': '#00e676', 'Clear Win B': 'var(--accent)',
  'Lopsided A': '#00e676', 'Lopsided B': '#ff4757',
};

const WINDOW_COLORS = {
  'Good Fit': '#00e676', 'Neutral': '#ffd32a', 'Poor Fit': '#ff4757',
};

// ── Pick Selector ──────────────────────────────────────────────────────────
function PickSelector({ picks, onChange }) {
  const addPick = () => {
    onChange([...picks, { year: 2027, round: 1, descriptor: 'mid' }]);
  };
  const removePick = (i) => {
    onChange(picks.filter((_, idx) => idx !== i));
  };
  const updatePick = (i, field, value) => {
    const updated = picks.map((p, idx) => idx === i ? { ...p, [field]: value } : p);
    onChange(updated);
  };
  return (
    <div className="dyn-ta-picks">
      {picks.map((pick, i) => (
        <div key={i} className="dyn-ta-pick-row">
          <select value={pick.year} onChange={e => updatePick(i, 'year', Number(e.target.value))}
            style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', padding: '4px 6px', fontSize: '0.75rem' }}>
            {[2027, 2028, 2029].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={pick.round} onChange={e => updatePick(i, 'round', Number(e.target.value))}
            style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', padding: '4px 6px', fontSize: '0.75rem' }}>
            {[1, 2, 3].map(r => <option key={r} value={r}>Round {r}</option>)}
          </select>
          <select value={pick.descriptor} onChange={e => updatePick(i, 'descriptor', e.target.value)}
            style={{ background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', padding: '4px 6px', fontSize: '0.75rem' }}>
            {['early', 'mid', 'late'].map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <button onClick={() => removePick(i)}
            style={{ background: 'rgba(255,71,87,0.15)', border: 'none', borderRadius: 4, color: 'var(--red)', cursor: 'pointer', padding: '4px 8px', fontSize: '0.72rem' }}>
            ✕
          </button>
        </div>
      ))}
      <button className="dyn-ta-add-pick" onClick={addPick}>
        + Add Pick
      </button>
    </div>
  );
}

// ── Player Selector ────────────────────────────────────────────────────────
function PlayerSelector({ roster, selectedIds, onChange }) {
  const [search, setSearch] = useState('');
  const POSITIONS = ['QB', 'RB', 'WR', 'TE'];
  const filtered = (roster || []).filter(p =>
    POSITIONS.includes(p.position) &&
    (search === '' || p.name.toLowerCase().includes(search.toLowerCase()))
  );

  const toggle = (pid) => {
    if (selectedIds.includes(pid)) {
      onChange(selectedIds.filter(id => id !== pid));
    } else {
      onChange([...selectedIds, pid]);
    }
  };

  return (
    <div className="dyn-ta-selector">
      <input
        className="dyn-ta-search"
        placeholder="Search players..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <div className="dyn-ta-roster">
        {filtered.map(p => {
          const selected = selectedIds.includes(p.player_id);
          return (
            <button type="button" key={p.player_id} onClick={() => toggle(p.player_id)} className={`dyn-ta-player${selected ? ' selected' : ''}`}>
              <span className="dyn-ta-pos" style={{ color: POS_COLORS[p.position] || 'var(--accent)' }}>{p.position}</span>
              <span className="dyn-ta-player-main"><strong>{p.name}</strong><small>{p.team || 'FA'} · Tier {p.tier}</small></span>
              <span className="dyn-ta-value">#{p.expert_rank || '—'}</span>
            </button>
          );
        })}
        {filtered.length === 0 && <div style={{ fontSize: '0.75rem', color: 'var(--text3)', padding: '8px 0' }}>No players found</div>}
      </div>
    </div>
  );
}

function FaabControl({ side, faab, value, onChange }) {
  if (!faab) return null;
  return (
    <div className="dyn-ta-faab-control">
      <div><label htmlFor={`dyn-faab-${side}`}>FAAB INCLUDED</label><small>${faab.remaining} currently available</small></div>
      <div className="dyn-ta-faab-input"><span>$</span><input id={`dyn-faab-${side}`} type="number" min="0" max={faab.remaining} step="1" value={value} onChange={e => onChange(e.target.value)} /></div>
    </div>
  );
}

function PackageDestination({ teams, teamId, value, onChange }) {
  return <label className="dyn-ta-destination">SEND THIS PACKAGE TO
    <select value={value || ''} onChange={event => onChange(Number(event.target.value))}>
      <option value="">Choose recipient…</option>
      {teams.filter(team => String(team.roster_id) !== String(teamId)).map(team =>
        <option value={team.roster_id} key={team.roster_id}>{team.display_name}</option>)}
    </select>
  </label>;
}

// ── Depth Grid ─────────────────────────────────────────────────────────────
function DepthGrid({ before, after, label }) {
  const positions = ['QB', 'RB', 'WR', 'TE'];
  const gradeColor = { Strong: '#00e676', Adequate: '#ffd32a', Thin: '#ff4757' };
  return (
    <div>
      <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, fontFamily: 'var(--font-mono)' }}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {positions.map(pos => {
          const b = before?.[pos] || {};
          const a = after?.[pos] || {};
          const changed = b.grade !== a.grade;
          return (
            <div key={pos} style={{
              background: 'var(--bg4)', border: `1px solid ${changed ? (gradeColor[a.grade] || '#888') + '44' : 'var(--border)'}`,
              borderRadius: 6, padding: '8px 10px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: POS_COLORS[pos], fontFamily: 'var(--font-mono)', marginBottom: 4 }}>{pos}</div>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: gradeColor[a.grade] || '#888' }}>{a.grade || '—'}</div>
              {changed && (
                <div style={{ fontSize: '0.6rem', color: 'var(--text3)', marginTop: 2 }}>
                  was {b.grade}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Evaluation Result ──────────────────────────────────────────────────────
function EvalResult({ result }) {
  if (!result) return null;
  if (result.multi_team) return <MultiTeamEvalResult result={result} />;
  const ai = result.ai || {};
  const fairnessColor = FAIRNESS_COLORS[result.fairness] || '#888';
  const gradeAColor = GRADE_COLORS[ai.grade_a] || '#888';
  const gradeBColor = GRADE_COLORS[ai.grade_b] || '#888';

  return (
    <div style={{ marginTop: 24 }}>
      {/* Verdict banner */}
      <div style={{
        background: fairnessColor + '12',
        border: `1px solid ${fairnessColor}33`,
        borderRadius: 12, padding: '16px 20px', marginBottom: 16,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${fairnessColor}, transparent)` }} />
        <div style={{ fontSize: '0.62rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>Verdict</div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: fairnessColor, letterSpacing: '1px' }}>
          {ai.verdict || result.fairness}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text3)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
          Value received: {result.team_a.display_name} {result.side_b.total_value} pts · {result.team_b.display_name} {result.side_a.total_value} pts · {Math.abs(result.pct_diff).toFixed(1)}% gap
        </div>
      </div>

      {/* Grade cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[
          { team: result.team_a, grade: ai.grade_a, window: ai.window_alignment_a, color: gradeAColor, faab: result.side_a.faab },
          { team: result.team_b, grade: ai.grade_b, window: ai.window_alignment_b, color: gradeBColor, faab: result.side_b.faab },
        ].map((side, i) => (
          <div key={i} style={{
            background: (side.color || '#888') + '08',
            border: `1px solid ${(side.color || '#888')}22`,
            borderRadius: 10, padding: '14px 16px',
          }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 8 }}>{side.team.display_name}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: side.color, marginBottom: 6 }}>{side.grade}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.62rem', padding: '2px 7px', borderRadius: 4,
                background: (WINDOW_COLORS[side.window] || '#888') + '18',
                color: WINDOW_COLORS[side.window] || '#888',
                fontFamily: 'var(--font-mono)',
              }}>
                {side.window || '—'}
              </span>
              <span style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                {side.team.window}
              </span>
            </div>
            {side.faab && <div className="dyn-ta-result-faab">FAAB: ${side.faab.before} → ${side.faab.after}</div>}
          </div>
        ))}
      </div>

      {/* AI Narrative */}
      {ai.narrative && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            📊 Analysis
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--text2)' }}>{ai.narrative}</div>
        </div>
      )}

      {/* Counteroffer */}
      {ai.counteroffer && (
        <div className="card" style={{ marginBottom: 12, background: 'rgba(var(--accent-rgb),0.04)', borderColor: 'rgba(var(--accent-rgb),0.2)' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            💡 Suggested Counteroffer
          </div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.7, color: 'var(--text)' }}>{ai.counteroffer}</div>
        </div>
      )}

      {/* Pick Exchange values */}
      {(result.pe_picks_a?.length > 0 || result.pe_picks_b?.length > 0) && (
        <div className="card" style={{ marginBottom: 12, background: 'rgba(var(--accent-rgb),0.03)', borderColor: 'rgba(var(--accent-rgb),0.15)' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            📊 Pick Exchange Values
          </div>
          {[...(result.pe_picks_a || []), ...(result.pe_picks_b || [])].map((p, i) => (
            <div key={i} style={{ fontSize: '0.78rem', color: 'var(--text2)', marginBottom: 6, lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{p.label}</strong> — projected {p.slot_label}, market value {p.market_value} pts ({p.confidence} confidence, {p.market_label}). {p.explanation}
            </div>
          ))}
        </div>
      )}

      {/* Risk flags */}
      {ai.risk_flags?.length > 0 && (
        <div className="card" style={{ marginBottom: 12, background: 'rgba(255,71,87,0.04)', borderColor: 'rgba(255,71,87,0.15)' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            ⚠️ Risk Flags
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ai.risk_flags.map((flag, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text2)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--red)', flexShrink: 0 }}>•</span>
                <span>{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.current_impact && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
            ⚡ Projected Lineup Impact
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['a', result.team_a], ['b', result.team_b]].map(([key, team]) => {
              const impact = result.current_impact[key];
              return <div key={key} style={{ padding: 12, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8 }}>
                <strong style={{ fontSize: '.8rem' }}>{team.display_name}</strong>
                <div style={{ marginTop: 7, color: impact.delta > 0 ? 'var(--green)' : impact.delta < 0 ? 'var(--red)' : 'var(--yellow)', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                  {impact.delta >= 0 ? '+' : ''}{impact.delta} projected points
                </div>
                <small style={{ color: 'var(--text3)' }}>{impact.before.projected_points} → {impact.after.projected_points} best legal lineup</small>
              </div>;
            })}
          </div>
        </div>
      )}

      {/* Depth impact */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="card">
          <DepthGrid before={result.depth_before?.a} after={result.depth_after?.a} label={`${result.team_a.display_name} Roster Impact`} />
        </div>
        <div className="card">
          <DepthGrid before={result.depth_before?.b} after={result.depth_after?.b} label={`${result.team_b.display_name} Roster Impact`} />
        </div>
      </div>

      <div style={{ fontSize: '0.68rem', color: 'var(--text3)', textAlign: 'center', marginTop: 16, fontStyle: 'italic' }}>
        ⚠️ This is analytical guidance, not a recommendation. Dynasty managers have context the system cannot fully capture.
      </div>
    </div>
  );
}

function MultiTeamEvalResult({ result }) {
  return <div className="dyn-multi-result">
    <section className="card dyn-multi-verdict"><small>THREE-TEAM VERDICT</small><h2>{result.verdict}</h2><strong>{result.edge_to ? `Strongest result: ${result.edge_to}` : 'Balanced exchange'}</strong><p>{result.narrative}</p></section>
    <div className="dyn-multi-results-grid">{(result.teams || []).map(side => <article className="card dyn-multi-side" key={side.roster_id}>
      <header><div><small>{side.window}</small><h3>{side.team_name}</h3></div><div><b>{side.grade}</b><small>{side.grade_score}/100</small></div></header>
      <div className="dyn-multi-values"><span>Sent <b>{side.sent_value}</b></span><span>Received <b>{side.received_value}</b></span><span className={side.net_value >= 0 ? 'positive' : 'negative'}>Net <b>{side.net_value >= 0 ? '+' : ''}{side.net_value}</b></span></div>
      <p>{side.fit_summary}</p>
      <div className="dyn-multi-flow"><strong>SENDS TO {side.sends_to}</strong>{side.sends.map((asset, index) => <span key={`out-${asset.name}-${index}`}>{asset.name}</span>)}</div>
      <div className="dyn-multi-flow receives"><strong>RECEIVES FROM {(side.receives_from || []).join(' + ')}</strong>{side.receives.map((asset, index) => <span key={`in-${asset.name}-${index}`}>{asset.name}</span>)}</div>
      <div className="dyn-multi-impact"><span>Best lineup <b>{side.lineup_delta >= 0 ? '+' : ''}{side.lineup_delta}</b></span><span>Depth <b>{side.depth_delta >= 0 ? '+' : ''}{side.depth_delta}</b></span><span>FAAB <b>${side.faab.before} → ${side.faab.after}</b></span></div>
    </article>)}</div>
    <p className="dyn-multi-method">{result.methodology?.note}</p>
  </div>;
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function TradeLab({ api, status }) {
  const { currentUser, csrfToken, refreshCsrf } = useUser();
  const [activeTab, setActiveTab] = useState('evaluator');
  const [standings, setStandings] = useState([]);
  const [rosters, setRosters] = useState({});

  // AI Suggestions state
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [rosterDetail, setRosterDetail] = useState(null);
  const [trades, setTrades] = useState(null);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [tradeError, setTradeError] = useState('');

  // Evaluator state
  const [teamA, setTeamA] = useState(null);
  const [teamB, setTeamB] = useState(null);
  const [teamC, setTeamC] = useState(null);
  const [rosterA, setRosterA] = useState(null);
  const [rosterB, setRosterB] = useState(null);
  const [rosterC, setRosterC] = useState(null);
  const [playersA, setPlayersA] = useState([]);
  const [playersB, setPlayersB] = useState([]);
  const [playersC, setPlayersC] = useState([]);
  const [picksA, setPicksA] = useState([]);
  const [picksB, setPicksB] = useState([]);
  const [picksC, setPicksC] = useState([]);
  const [faabA, setFaabA] = useState('0');
  const [faabB, setFaabB] = useState('0');
  const [faabC, setFaabC] = useState('0');
  const [threeTeam, setThreeTeam] = useState(false);
  const [toA, setToA] = useState(null);
  const [toB, setToB] = useState(null);
  const [toC, setToC] = useState(null);
  const [evalResult, setEvalResult] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evalError, setEvalError] = useState('');
  const [peValues, setPeValues] = useState(null);  // PE pick value map or null

  const isCommissioner =
    currentUser?.display_name === 'PhillyxCommish' ||
    currentUser?.username === 'PhillyxCommish';
  const ownRosterId = currentUser?.roster_id;
  const lockOwnTeam = Boolean(!currentUser?.guest && ownRosterId);

  useEffect(() => {
    const load = async () => {
      const r = await fetch(`${api}/api/standings`);
      const d = await r.json();
      setStandings(d);
      if (currentUser?.roster_id) {
        setSelectedRoster(currentUser.roster_id);
        setTeamA(currentUser.roster_id);
      } else if (d.length > 0) {
        setSelectedRoster(d[0].roster_id);
        setTeamA(d[0].roster_id);
        if (d.length > 1) setTeamB(d[1].roster_id);
      }
    };
    load();
    // Load PE pick values (silently — feature may be disabled)
    fetch(`${api}/api/pick-exchange/values`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.enabled && d.values) setPeValues(d.values); })
      .catch(() => {});
  }, [api, isCommissioner, currentUser]);

  useEffect(() => {
    if (lockOwnTeam && String(teamA) !== String(ownRosterId)) {
      setTeamA(Number(ownRosterId));
      setPlayersA([]);
      setPicksA([]);
      setEvalResult(null);
    }
  }, [lockOwnTeam, ownRosterId, teamA]);

  // Load roster for suggestions tab
  useEffect(() => {
    if (!selectedRoster) return;
    const load = async () => {
      try {
        const r = await fetch(`${api}/api/roster/${selectedRoster}`);
        setRosterDetail(await r.json());
        setTrades(null);
        setTradeError('');
      } catch {}
    };
    load();
  }, [selectedRoster, api]);

  // Load rosters for evaluator
  useEffect(() => {
    if (!teamA) return;
    fetch(`${api}/api/roster/${teamA}`).then(r => r.json()).then(d => setRosterA(d)).catch(() => {});
    setFaabA('0');
  }, [teamA, api]);

  useEffect(() => {
    if (!teamB) return;
    fetch(`${api}/api/roster/${teamB}`).then(r => r.json()).then(d => setRosterB(d)).catch(() => {});
    setPlayersB([]);
    setPicksB([]);
    setFaabB('0');
  }, [teamB, api]);

  useEffect(() => {
    if (!teamC) return;
    fetch(`${api}/api/roster/${teamC}`).then(r => r.json()).then(d => setRosterC(d)).catch(() => {});
    setPlayersC([]); setPicksC([]); setFaabC('0');
  }, [teamC, api]);

  useEffect(() => {
    if (!threeTeam || !teamA || !teamB || !teamC) return;
    setToA(current => [teamB, teamC].includes(current) ? current : teamB);
    setToB(current => [teamA, teamC].includes(current) ? current : teamC);
    setToC(current => [teamA, teamB].includes(current) ? current : teamA);
  }, [threeTeam, teamA, teamB, teamC]);

  const requestTrades = async () => {
    setLoadingTrades(true);
    setTradeError('');
    setTrades(null);
    try {
      const r = await fetch(`${api}/api/trades/${selectedRoster}`, { method: 'POST' });
      const d = await r.json();
      if (d.error) setTradeError(d.error);
      else setTrades(d);
    } catch {
      setTradeError('Failed to fetch trades. Is the AI key configured?');
    }
    setLoadingTrades(false);
  };

  const runEvaluation = async () => {
    if (!teamA || !teamB) { setEvalError('Select both teams'); return; }
    if (teamA === teamB) { setEvalError('Select two different teams'); return; }
    if (threeTeam && (!teamC || new Set([teamA, teamB, teamC]).size !== 3)) { setEvalError('Select three different teams'); return; }
    if (playersA.length === 0 && picksA.length === 0 && Number(faabA) <= 0) { setEvalError('Add at least one player, pick, or FAAB to Side A'); return; }
    if (playersB.length === 0 && picksB.length === 0 && Number(faabB) <= 0) { setEvalError('Add at least one player, pick, or FAAB to Side B'); return; }
    if (threeTeam && playersC.length === 0 && picksC.length === 0 && Number(faabC) <= 0) { setEvalError('Add at least one player, pick, or FAAB to Team C'); return; }

    setEvaluating(true);
    setEvalError('');
    setEvalResult(null);

    try {
      const submit = token => fetch(`${api}/api/evaluate-trade`, {
        method: 'POST', credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify(threeTeam ? { participants: [
          { roster_id: teamA, to_roster_id: toA, players: playersA, picks: picksA, faab: Number(faabA || 0) },
          { roster_id: teamB, to_roster_id: toB, players: playersB, picks: picksB, faab: Number(faabB || 0) },
          { roster_id: teamC, to_roster_id: toC, players: playersC, picks: picksC, faab: Number(faabC || 0) },
        ] } : {
          roster_id_a: teamA, roster_id_b: teamB,
          players_a: playersA, players_b: playersB,
          picks_a: picksA, picks_b: picksB,
          faab_a: Number(faabA || 0), faab_b: Number(faabB || 0),
        }),
      });
      let token = csrfToken || await refreshCsrf?.();
      let r = await submit(token || '');
      let data = await r.json();
      if (r.status === 403 && data.detail === 'CSRF validation failed') {
        token = await refreshCsrf?.();
        if (token) { r = await submit(token); data = await r.json(); }
      }
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setEvalResult(data);
    } catch (e) {
      setEvalError(`Evaluation failed: ${e.message}`);
    }
    setEvaluating(false);
  };

  if (!status?.has_ai) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Trade Analyzer</div>
          <div className="page-subtitle">Two- or three-team trades with live values, roster fit and legal lineup impact.</div>
        </div>
        <div className="empty-state" style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: 40 }}>
          <div style={{ fontSize: '3rem', marginBottom: 16 }}>🤖</div>
          <p style={{ marginBottom: 8, fontWeight: 600, color: 'var(--text)' }}>AI Trade Engine Not Configured</p>
          <p style={{ maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
            Add your <code style={{ color: 'var(--accent)' }}>ANTHROPIC_API_KEY</code> to enable AI trade analysis.
          </p>
        </div>
      </div>
    );
  }

  const myTeam = standings.find(s => s.roster_id === currentUser?.roster_id);

  // PE: look up market value for a pick by year:round:descriptor
  const getPeInfo = (pick) => {
    if (!peValues || !pick) return null;
    const key = `${pick.year}:${pick.round}:${pick.descriptor || 'mid'}`;
    return peValues[key] || null;
  };

  // Dangerous pick exposure: own 1st round pick projecting 1.01–1.04 being traded away
  const dangerousPicksA = useMemo(() => {
    if (!peValues || !teamA) return [];
    return picksA.filter(p => {
      if (p.round !== 1) return false;
      const info = getPeInfo(p);
      if (!info) return false;
      const slot = parseInt(info.slot_label?.split('.')?.[1] || '10', 10);
      return slot <= 4;
    });
  }, [picksA, peValues, teamA]); // eslint-disable-line

  const dangerousPicksB = useMemo(() => {
    if (!peValues || !teamB) return [];
    return picksB.filter(p => {
      if (p.round !== 1) return false;
      const info = getPeInfo(p);
      if (!info) return false;
      const slot = parseInt(info.slot_label?.split('.')?.[1] || '10', 10);
      return slot <= 4;
    });
  }, [picksB, peValues, teamB]); // eslint-disable-line

  const dangerousPicksC = useMemo(() => {
    if (!peValues || !teamC) return [];
    return picksC.filter(p => {
      if (p.round !== 1) return false;
      const info = getPeInfo(p);
      if (!info) return false;
      const slot = parseInt(info.slot_label?.split('.')?.[1] || '10', 10);
      return slot <= 4;
    });
  }, [picksC, peValues, teamC]); // eslint-disable-line

  const tabs = [
    { key: 'evaluator', label: '⚖️ Trade Evaluator' },
    { key: 'suggestions', label: '🤖 AI Suggestions' },
  ];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Trade Analyzer</div>
        <div className="page-subtitle">Two- or three-team trades with live values, roster fit and legal lineup impact.</div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`tab-btn${activeTab === t.key ? ' active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TRADE EVALUATOR TAB ── */}
      {activeTab === 'evaluator' && (
        <div>
          <div className="dyn-ta-mode"><button className={!threeTeam ? 'active' : ''} onClick={() => { setThreeTeam(false); setEvalResult(null); }}>2 Teams</button><button className={threeTeam ? 'active' : ''} onClick={() => { setThreeTeam(true); setEvalResult(null); }}>+ Add Third Team</button></div>
          {threeTeam && <p className="dyn-ta-flow-help">Build each team’s outgoing package, then choose which of the other two franchises receives it.</p>}
          <div className={`dyn-ta-grid${threeTeam ? ' three-team' : ''}`}>

            {/* Side A */}
            <div className="card dyn-ta-side">
              <div className="dyn-ta-label">{lockOwnTeam ? 'YOUR TEAM SENDS' : 'TEAM A SENDS'}</div>
              <select className="dyn-ta-select" value={teamA || ''} disabled={lockOwnTeam} onChange={e => { setTeamA(Number(e.target.value)); setPlayersA([]); setPicksA([]); setEvalResult(null); }}
                style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', fontSize: '0.82rem', marginBottom: 12 }}>
                <option value="">Select team...</option>
                {standings.map(s => <option key={s.roster_id} value={s.roster_id}>{s.display_name}</option>)}
              </select>
              {lockOwnTeam && <div className="dyn-ta-locked">🔒 Locked to your logged-in Dynasty franchise</div>}
              {threeTeam && <PackageDestination teams={standings.filter(team => [teamA, teamB, teamC].includes(team.roster_id))} teamId={teamA} value={toA} onChange={setToA} />}
              <FaabControl side="a" faab={rosterA?.faab} value={faabA} onChange={setFaabA} />

              {rosterA && (
                <>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Select Players</div>
                  <PlayerSelector roster={rosterA.roster} selectedIds={playersA} onChange={setPlayersA} />
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, margin: '12px 0 6px', fontFamily: 'var(--font-mono)' }}>Draft Picks</div>
                  <PickSelector picks={picksA} onChange={setPicksA} />
                </>
              )}

            </div>

            {/* Side B */}
            <div className="card dyn-ta-side">
              <div className="dyn-ta-label">TEAM B SENDS</div>
              <select className="dyn-ta-select" value={teamB || ''} onChange={e => { setTeamB(Number(e.target.value)); setPlayersB([]); setPicksB([]); setEvalResult(null); }}
                style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', fontSize: '0.82rem', marginBottom: 12 }}>
                <option value="">Select team...</option>
                {standings.filter(s => String(s.roster_id) !== String(teamA) && (!threeTeam || String(s.roster_id) !== String(teamC))).map(s => <option key={s.roster_id} value={s.roster_id}>{s.display_name}</option>)}
              </select>
              {threeTeam && <PackageDestination teams={standings.filter(team => [teamA, teamB, teamC].includes(team.roster_id))} teamId={teamB} value={toB} onChange={setToB} />}
              <FaabControl side="b" faab={rosterB?.faab} value={faabB} onChange={setFaabB} />

              {rosterB && (
                <>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Select Players</div>
                  <PlayerSelector roster={rosterB.roster} selectedIds={playersB} onChange={setPlayersB} />
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, margin: '12px 0 6px', fontFamily: 'var(--font-mono)' }}>Draft Picks</div>
                  <PickSelector picks={picksB} onChange={setPicksB} />
                </>
              )}

            </div>

            {threeTeam && <div className="card dyn-ta-side">
              <div className="dyn-ta-label">TEAM C SENDS</div>
              <select className="dyn-ta-select" value={teamC || ''} onChange={e => { setTeamC(Number(e.target.value)); setPlayersC([]); setPicksC([]); setEvalResult(null); }}
                style={{ width: '100%', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', fontSize: '0.82rem', marginBottom: 12 }}>
                <option value="">Select team...</option>
                {standings.filter(s => String(s.roster_id) !== String(teamA) && String(s.roster_id) !== String(teamB)).map(s => <option key={s.roster_id} value={s.roster_id}>{s.display_name}</option>)}
              </select>
              <PackageDestination teams={standings.filter(team => [teamA, teamB, teamC].includes(team.roster_id))} teamId={teamC} value={toC} onChange={setToC} />
              <FaabControl side="c" faab={rosterC?.faab} value={faabC} onChange={setFaabC} />
              {rosterC && <><div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, fontFamily: 'var(--font-mono)' }}>Select Players</div>
                <PlayerSelector roster={rosterC.roster} selectedIds={playersC} onChange={setPlayersC} />
                <div style={{ fontSize: '0.65rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, margin: '12px 0 6px', fontFamily: 'var(--font-mono)' }}>Draft Picks</div>
                <PickSelector picks={picksC} onChange={setPicksC} /></>}
            </div>}
          </div>

          {/* Dangerous pick exposure warnings */}
          {dangerousPicksA.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.35)', borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 6 }}>⚠️ Dangerous Pick Exposure</div>
              {dangerousPicksA.map((p, i) => {
                const pe = getPeInfo(p);
                return (
                  <div key={i} style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5 }}>
                    Side A is trading away a <strong>{pe?.slot_label}</strong> projected pick ({p.year} Round {p.round}) — a top-4 asset. Confirm this is intentional.
                  </div>
                );
              })}
            </div>
          )}
          {dangerousPicksB.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.35)', borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 6 }}>⚠️ Dangerous Pick Exposure</div>
              {dangerousPicksB.map((p, i) => {
                const pe = getPeInfo(p);
                return (
                  <div key={i} style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5 }}>
                    Side B is trading away a <strong>{pe?.slot_label}</strong> projected pick ({p.year} Round {p.round}) — a top-4 asset. Confirm this is intentional.
                  </div>
                );
              })}
            </div>
          )}
          {threeTeam && dangerousPicksC.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.35)', borderRadius: 8, marginBottom: 10 }}>
              <div style={{ fontSize: '0.65rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'var(--font-mono)', fontWeight: 700, marginBottom: 6 }}>⚠️ Dangerous Pick Exposure</div>
              {dangerousPicksC.map((p, i) => { const pe = getPeInfo(p); return <div key={i} style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5 }}>Team C is trading away a <strong>{pe?.slot_label}</strong> projected pick ({p.year} Round {p.round}) — a top-4 asset. Confirm this is intentional.</div>; })}
            </div>
          )}

          {evalError && (
            <div style={{ color: 'var(--red)', fontSize: '0.82rem', marginBottom: 12, padding: '8px 12px', background: 'rgba(255,71,87,0.08)', borderRadius: 6 }}>
              ⚠️ {evalError}
            </div>
          )}

          <button className="dyn-ta-evaluate" onClick={runEvaluation} disabled={evaluating}>
            {evaluating ? 'Analyzing…' : 'Analyze Trade'}
          </button>

          {evaluating && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text2)', fontSize: '0.85rem' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              Analyzing rosters, windows, and value...
            </div>
          )}

          <EvalResult result={evalResult} />
        </div>
      )}

      {/* ── AI SUGGESTIONS TAB ── */}
      {activeTab === 'suggestions' && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: '0.75rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'var(--font-mono)' }}>
              {isCommissioner ? 'Select Team' : 'Your Team'}
            </label>
            {isCommissioner ? (
              <select className="h2h-select" value={selectedRoster || ''} onChange={e => setSelectedRoster(Number(e.target.value))} style={{ marginTop: 4 }}>
                {standings.map(s => <option key={s.roster_id} value={s.roster_id}>{s.display_name} ({s.wins}-{s.losses})</option>)}
              </select>
            ) : (
              <div style={{ marginTop: 4, padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                {myTeam && <Avatar avatar={myTeam.avatar} name={myTeam.display_name} size={28} />}
                <span style={{ fontSize: '0.88rem', color: 'var(--text)', fontWeight: 500 }}>{myTeam?.display_name || currentUser?.display_name}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginLeft: 4 }}>{myTeam ? `${myTeam.wins}-${myTeam.losses}` : ''}</span>
              </div>
            )}
          </div>

          {rosterDetail && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Avatar avatar={rosterDetail.team.avatar} name={rosterDetail.team.display_name} size={40} />
                <div>
                  <div style={{ fontWeight: 700 }}>{rosterDetail.team.display_name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>
                    {rosterDetail.team.wins}-{rosterDetail.team.losses} · Playoff: {rosterDetail.team.playoff_pct}% · Champ: {rosterDetail.team.championship_pct}%
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, fontFamily: 'var(--font-mono)' }}>Top Roster</div>
              {rosterDetail.roster.length === 0 ? (
                <div style={{ color: 'var(--text3)', fontSize: '0.85rem' }}>Loading expert rankings...</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
                  {rosterDetail.roster.slice(0, 12).map(p => (
                    <div key={p.player_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 6, border: '1px solid var(--border)' }}>
                      <span style={{ width: 28, height: 28, background: (POS_COLORS[p.position] || '#888') + '22', color: POS_COLORS[p.position] || '#888', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{p.position}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{p.team} · ECR #{p.expert_rank} · T{p.tier}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn" onClick={requestTrades} disabled={loadingTrades || !rosterDetail?.roster?.length}
              style={{ background: loadingTrades ? 'var(--bg4)' : 'linear-gradient(135deg, var(--accent), var(--accent2))', color: '#000', fontSize: '0.92rem', padding: '12px 24px', fontWeight: 700, letterSpacing: 0.5, opacity: loadingTrades ? 0.6 : 1 }}>
              {loadingTrades ? '🤖 Analyzing rosters...' : '🤖 Generate Trade Suggestions'}
            </button>
            <span style={{ fontSize: '0.72rem', color: 'var(--text3)', alignSelf: 'center' }}>~$0.01 per analysis · Takes 10-30 sec</span>
          </div>

          {tradeError && <div className="card" style={{ background: 'rgba(255,71,87,0.06)', borderColor: 'rgba(255,71,87,0.3)' }}><div style={{ color: 'var(--red)', fontSize: '0.85rem' }}>⚠️ {tradeError}</div></div>}

          {loadingTrades && (
            <div className="card" style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" style={{ margin: '0 auto 16px' }} />
              <div style={{ color: 'var(--text2)', fontSize: '0.88rem' }}>Analyzing rosters and player rankings — this takes about 15 seconds...</div>
            </div>
          )}

          {trades?.suggestions?.length > 0 && (
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12, fontFamily: 'var(--font-mono)' }}>
                ⚡ {trades.suggestions.length} Trades Recommended
              </div>
              {trades.suggestions.map((trade, i) => (
                <div key={i} className="card" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', letterSpacing: 1.5 }}>TRADE #{i + 1} — vs {trade.partner}</div>
                    <div style={{ background: trade.confidence >= 7 ? 'rgba(0,230,118,0.15)' : trade.confidence >= 4 ? 'rgba(255,211,42,0.15)' : 'rgba(255,71,87,0.15)', color: trade.confidence >= 7 ? 'var(--green)' : trade.confidence >= 4 ? 'var(--yellow)' : 'var(--red)', padding: '4px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                      {trade.confidence}/10 LIKELY
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ background: 'rgba(255,71,87,0.04)', padding: 12, borderRadius: 8, borderLeft: '3px solid var(--red)' }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6, fontWeight: 600 }}>You Send</div>
                      {trade.send.map((p, j) => <div key={j} style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: 2 }}>{p}</div>)}
                    </div>
                    <div style={{ fontSize: '1.4rem', color: 'var(--text3)' }}>⇄</div>
                    <div style={{ background: 'rgba(0,230,118,0.04)', padding: 12, borderRadius: 8, borderLeft: '3px solid var(--green)' }}>
                      <div style={{ fontSize: '0.65rem', color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6, fontWeight: 600 }}>You Get</div>
                      {trade.receive.map((p, j) => <div key={j} style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: 2 }}>{p}</div>)}
                    </div>
                  </div>
                  <div style={{ background: 'var(--bg3)', padding: 12, borderRadius: 6, marginBottom: 8 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, fontFamily: 'var(--font-mono)' }}>Why It Helps You</div>
                    <div style={{ fontSize: '0.85rem', lineHeight: 1.5, color: 'var(--text)' }}>{trade.rationale}</div>
                  </div>
                  <div style={{ background: 'var(--bg3)', padding: 12, borderRadius: 6 }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--accent2)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, fontFamily: 'var(--font-mono)' }}>Why They'd Accept</div>
                    <div style={{ fontSize: '0.85rem', lineHeight: 1.5, color: 'var(--text)' }}>{trade.fair_for_partner}</div>
                  </div>
                </div>
              ))}
              <div style={{ fontSize: '0.7rem', color: 'var(--text3)', textAlign: 'center', marginTop: 16, fontStyle: 'italic' }}>
                ⚠️ AI suggestions are starting points. Always verify player values yourself before proposing.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
